import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { num } from '../format';
import { useReportWidget } from '../hooks/useReportWidget';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { COLORS, cardSx } from '../theme';
import { ROWS_PER_PAGE_OPTIONS, paginateRows } from './Conversions';
import { pageWindow } from './ImpressionsByType';

const toNum = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Ranks every fetched event row by fire count, descending — the ordering this
 * card has always shown, extracted to its own export (was inline in the
 * render) so adding pagination below can slice it with `paginateRows` rather
 * than re-deriving it, and so the "sort the full set, not just a page" rule
 * (flank#4742 AC2's requirement, mirrored here) is unit-testable without
 * mounting the widget.
 */
export const sortEventsByFires = <T extends { event_fires?: unknown }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => toNum(b.event_fires) - toNum(a.event_fires));

// Same 20/50/100 toggle styling Conversions.tsx and ImpressionsByType.tsx each
// define locally (prep #0000) — not extracted, matching how those two already
// keep their own copies rather than sharing one.
const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

/**
 * Web conversions by event — across all channels (flank#4647).
 *
 * The "Conversions by Type" By channel / By platform card that used to sit
 * above this table was removed (prep #0000, product meeting decision —
 * "Remove Conversion Cards"): it summed conversions across every DSP that ran
 * on a channel or platform, which double-counts an attributed conversion the
 * moment more than one platform claims it. Martin caught this live — the card
 * showed 36,000 web conversions for a scope where the per-entity Conversions
 * table (Conversions.tsx) showed a max of 9. Removed per product decision;
 * cross-platform conversion totals overlap, so they shouldn't be summed. The
 * card's own `groupBy` already carried a CONVERSIONS_NOT_ADDITIVE_NOTE caveat
 * for exactly this overlap; the decision was to stop summing across
 * platforms rather than caveat it further.
 *
 * The per-channel/per-platform breakdowns aren't gone — they still exist on
 * the Conversions widget's own Channel/Platform tabs (Conversions.tsx), which
 * read one row per entity rather than an aggregated cross-platform sum. This
 * component now renders only the event table, which was never affected by
 * the overlap (event_fires isn't attribution-summed the same way).
 */
const ConversionsBreakdown = () => {
  const { canViewAttribution } = useReportingPermissions();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);

  const {
    rows: eventRows,
    totalRows: eventsTotalRows,
    loading: eventsLoading,
    error: eventsError,
  } = useReportWidget('conversionEvents', {
    dimensions: ['event_name'],
    metrics: ['event_fires'],
    limit: 200,
  });

  // A capped request (review, CodeRabbit) can omit event rows with nothing on
  // screen saying so — same disclosure convention reportExport.ts's
  // truncationNote uses for exported sections, adapted here since this card
  // has no on-screen pager to point to.
  const eventsTruncated = eventsTotalRows > eventRows.length;

  // Sorted once here (was inline in the render below) so pagination can slice
  // the same ordered list rather than re-sorting per render (prep #0000 —
  // this table had no pager at all and rendered every fetched row at once).
  const sortedEvents = useMemo(() => sortEventsByFires(eventRows), [eventRows]);

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setPage(0);
  };

  const { pageRows, pageCount, safePage } = paginateRows(sortedEvents, page, rowsPerPage);

  // Same server-side Attribution permission as the Conversions widget
  // (flank#4648) — reused directly via useReportingPermissions rather than
  // re-derived from this widget's own restrictedFields, since event_fires
  // carries no conversion_type/conversions field of its own to signal on.
  if (!canViewAttribution) return null;

  return (
    <Box className='rv2-card' sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
        Web Conversions by Event
      </Typography>
      <Typography variant='caption' sx={{ color: COLORS.muted }}>
        Across all channels. Event names are recorded per-advertiser, not per-line-item or per-channel, so this table
        can't be narrowed by channel — a true channel × event split needs a new event feed.
      </Typography>
      {eventsLoading ? (
        <Typography variant='body2' sx={{ color: COLORS.muted }}>
          Loading…
        </Typography>
      ) : eventsError ? (
        <Typography variant='body2' color='error'>
          Failed to load events.
        </Typography>
      ) : eventRows.length === 0 ? (
        <Typography variant='body2' sx={{ color: COLORS.muted }}>
          No events for the selected filters.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {pageRows.map(row => (
              <Box key={String(row.event_name)} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant='body2' sx={{ color: COLORS.muted }}>
                  {String(row.event_name ?? 'Unnamed event')}
                </Typography>
                <Typography variant='body2'>{num(row.event_fires)}</Typography>
              </Box>
            ))}
          </Box>

          {sortedEvents.length > ROWS_PER_PAGE_OPTIONS[0] && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                {(safePage * rowsPerPage + 1).toLocaleString()}–
                {(safePage * rowsPerPage + pageRows.length).toLocaleString()} of {sortedEvents.length.toLocaleString()}{' '}
                events
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant='caption' sx={{ color: COLORS.muted }}>
                    Rows
                  </Typography>
                  <ToggleButtonGroup
                    size='small'
                    exclusive
                    value={rowsPerPage}
                    onChange={(_e, v) => v && handleRowsPerPageChange(v)}
                    sx={darkToggleSx}
                  >
                    {ROWS_PER_PAGE_OPTIONS.map(n => (
                      <ToggleButton key={n} value={n}>
                        {n}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    size='small'
                    disabled={safePage === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
                  >
                    Prev
                  </Button>
                  {pageWindow(safePage, pageCount).map((n, i) =>
                    n === -1 ? (
                      <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                        …
                      </Typography>
                    ) : (
                      <Button
                        key={n}
                        size='small'
                        onClick={() => setPage(n)}
                        sx={{
                          textTransform: 'none',
                          minWidth: 32,
                          px: 1,
                          color: n === safePage ? '#fff' : COLORS.muted,
                          bgcolor: n === safePage ? COLORS.black : 'transparent',
                          '&:hover': { bgcolor: n === safePage ? '#000' : COLORS.pageBg },
                        }}
                      >
                        {n + 1}
                      </Button>
                    ),
                  )}
                  <Button
                    size='small'
                    disabled={safePage >= pageCount - 1}
                    onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                    sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
      {!eventsLoading && eventsTruncated && (
        <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block' }}>
          Showing {eventRows.length.toLocaleString()} of {eventsTotalRows.toLocaleString()} events — narrow your filters
          to see the rest.
        </Typography>
      )}
    </Box>
  );
};

export default ConversionsBreakdown;
